import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { Package, Send, ArrowDownLeft, CheckCircle2, Clock, AlertCircle, Plus, RotateCcw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SampleRegisterPage() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [samplesIssued, setSamplesIssued] = useState([]);
  const [samplesReceived, setSamplesReceived] = useState([]);
  const [salesReturns, setSalesReturns] = useState([]);
  const [clients, setClients] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [parties, setParties] = useState([]); // Combined clients + suppliers
  const [items, setItems] = useState([]);
  
  // Dialog states
  const [issueSampleDialog, setIssueSampleDialog] = useState(false);
  const [receiveSampleDialog, setReceiveSampleDialog] = useState(false);
  const [returnSampleDialog, setReturnSampleDialog] = useState(false);
  const [selectedSample, setSelectedSample] = useState(null);
  
  // Form states
  const [issueForm, setIssueForm] = useState({
    party_name: '',
    item_name: '',
    quantity: '',
    sample_type: 'Returnable',
    purpose: '',
    expected_return_date: ''
  });
  
  const [receiveForm, setReceiveForm] = useState({
    party_name: '',
    item_name: '',
    quantity: '',
    received_from: '',
    condition: 'good',
    action_taken: ''
  });
  
  const [returnForm, setReturnForm] = useState({
    returned_qty: 0,
    notes: ''
  });

  const getAuthHeader = useCallback(() => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [issuedRes, receivedRes, returnsRes, clientsRes, suppliersRes, itemsRes] = await Promise.all([
        axios.get(`${API_URL}/api/store/samples?register_type=issued`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/store/samples?register_type=received`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/store/sales-returns`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/clients`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/suppliers`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/items`, { headers: getAuthHeader() })
      ]);
      setSamplesIssued(issuedRes.data);
      setSamplesReceived(receivedRes.data);
      setSalesReturns(returnsRes.data);
      setClients(clientsRes.data);
      setSuppliers(suppliersRes.data || []);
      
      // Combine clients and suppliers into single parties list
      const allParties = [
        ...(clientsRes.data || []).map(c => ({ ...c, partyType: 'client' })),
        ...(suppliersRes.data || []).map(s => ({ ...s, partyType: 'supplier' }))
      ].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setParties(allParties);
      
      // Filter to FNG items only (category field is source of truth)
      setItems(itemsRes.data.filter(i => i.category?.toUpperCase() === 'FNG'));
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleIssueSample = async () => {
    const qty = parseFloat(issueForm.quantity);
    if (!issueForm.quantity || isNaN(qty) || qty <= 0) {
      toast({ title: 'Error', description: 'Quantity is required', variant: 'destructive' });
      return;
    }
    try {
      const params = new URLSearchParams({
        party_name: issueForm.party_name,
        item_name: issueForm.item_name,
        quantity: qty,
        sample_type: issueForm.sample_type,
        purpose: issueForm.purpose || ''
      });
      if (issueForm.expected_return_date) {
        params.append('expected_return_date', issueForm.expected_return_date);
      }
      
      const response = await axios.post(`${API_URL}/api/store/sample-issued?${params}`, {}, { headers: getAuthHeader() });
      toast({ title: 'Success', description: `Sample ${response.data.sample_no} issued successfully!` });
      setIssueSampleDialog(false);
      setIssueForm({ party_name: '', item_name: '', quantity: '', sample_type: 'Returnable', purpose: '', expected_return_date: '' });
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to issue sample', variant: 'destructive' });
    }
  };

  const handleReceiveSample = async () => {
    const qty = parseFloat(receiveForm.quantity);
    if (!receiveForm.quantity || isNaN(qty) || qty <= 0) {
      toast({ title: 'Error', description: 'Quantity is required', variant: 'destructive' });
      return;
    }
    try {
      const params = new URLSearchParams({
        party_name: receiveForm.party_name,
        item_name: receiveForm.item_name,
        quantity: qty,
        received_from: receiveForm.received_from || '',
        condition: receiveForm.condition,
        action_taken: receiveForm.action_taken || ''
      });
      
      const response = await axios.post(`${API_URL}/api/store/sample-received?${params}`, {}, { headers: getAuthHeader() });
      toast({ title: 'Success', description: `Sample ${response.data.sample_no} received successfully!` });
      setReceiveSampleDialog(false);
      setReceiveForm({ party_name: '', item_name: '', quantity: '', received_from: '', condition: 'good', action_taken: '' });
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to receive sample', variant: 'destructive' });
    }
  };

  const handleReturnSample = async () => {
    if (!selectedSample) return;
    try {
      await axios.patch(
        `${API_URL}/api/store/sample/${selectedSample.id}/return?returned_qty=${returnForm.returned_qty}&notes=${returnForm.notes || ''}`,
        {},
        { headers: getAuthHeader() }
      );
      toast({ title: 'Success', description: 'Sample return recorded successfully!' });
      setReturnSampleDialog(false);
      setSelectedSample(null);
      setReturnForm({ returned_qty: 0, notes: '' });
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to record return', variant: 'destructive' });
    }
  };

  const openReturnDialog = (sample) => {
    setSelectedSample(sample);
    setReturnForm({ returned_qty: sample.quantity - (sample.returned_qty || 0), notes: '' });
    setReturnSampleDialog(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getReturnStatusBadge = (status) => {
    switch (status) {
      case 'returned': return <Badge className="bg-green-100 text-green-800">Returned</Badge>;
      case 'partial': return <Badge className="bg-yellow-100 text-yellow-800">Partial</Badge>;
      case 'pending': return <Badge className="bg-orange-100 text-orange-800">Pending</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) return <DashboardLayout><div className="p-6 text-center">Loading...</div></DashboardLayout>;

  const pendingReturns = samplesIssued.filter(s => s.sample_type === 'Returnable' && s.return_status !== 'returned').length;

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold">Sample & Returns Register</h1>
          <p className="text-muted-foreground">Track samples issued, received, and sales returns</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setIssueSampleDialog(true)} className="bg-blue-600 hover:bg-blue-700">
            <Send className="w-4 h-4 mr-2" />
            Issue Sample
          </Button>
          <Button onClick={() => setReceiveSampleDialog(true)} variant="outline">
            <ArrowDownLeft className="w-4 h-4 mr-2" />
            Receive Sample
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Send className="w-8 h-8 text-blue-600" />
              <div>
                <p className="text-2xl font-bold">{samplesIssued.length}</p>
                <p className="text-sm text-muted-foreground">Samples Issued</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-orange-600" />
              <div>
                <p className="text-2xl font-bold">{pendingReturns}</p>
                <p className="text-sm text-muted-foreground">Pending Returns</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <ArrowDownLeft className="w-8 h-8 text-green-600" />
              <div>
                <p className="text-2xl font-bold">{samplesReceived.length}</p>
                <p className="text-sm text-muted-foreground">Samples Received</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <RotateCcw className="w-8 h-8 text-purple-600" />
              <div>
                <p className="text-2xl font-bold">{salesReturns.length}</p>
                <p className="text-sm text-muted-foreground">Sales Returns</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="issued" className="space-y-4">
        <TabsList>
          <TabsTrigger value="issued">Samples Issued ({samplesIssued.length})</TabsTrigger>
          <TabsTrigger value="received">Samples Received ({samplesReceived.length})</TabsTrigger>
          <TabsTrigger value="returns">Sales Returns ({salesReturns.length})</TabsTrigger>
        </TabsList>

        {/* Samples Issued Tab */}
        <TabsContent value="issued">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Samples Issued Register</CardTitle>
              <CardDescription>Track samples sent to clients/prospects</CardDescription>
            </CardHeader>
            <CardContent>
              {samplesIssued.length === 0 ? (
                <div className="text-center py-8">
                  <Send className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">No samples issued yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Sample No</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Party</TableHead>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-center">Qty</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Purpose</TableHead>
                      <TableHead>Return Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {samplesIssued.map((sample) => (
                      <TableRow key={sample.id}>
                        <TableCell className="font-medium">{sample.sample_no}</TableCell>
                        <TableCell>{formatDate(sample.date)}</TableCell>
                        <TableCell>{sample.party_name}</TableCell>
                        <TableCell className="max-w-[200px] truncate">{sample.item_name}</TableCell>
                        <TableCell className="text-center">{sample.quantity}</TableCell>
                        <TableCell>
                          <Badge variant={sample.sample_type === 'Returnable' ? 'default' : 'secondary'}>
                            {sample.sample_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-[150px] truncate">{sample.purpose || '-'}</TableCell>
                        <TableCell>
                          {sample.sample_type === 'Returnable' ? (
                            <div>
                              {getReturnStatusBadge(sample.return_status)}
                              {sample.returned_qty > 0 && (
                                <span className="text-xs ml-2 text-muted-foreground">
                                  ({sample.returned_qty}/{sample.quantity})
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">N/A</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {sample.sample_type === 'Returnable' && sample.return_status !== 'returned' && (
                            <Button size="sm" variant="outline" onClick={() => openReturnDialog(sample)}>
                              Record Return
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Samples Received Tab */}
        <TabsContent value="received">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Samples Received Register</CardTitle>
              <CardDescription>Track samples received from suppliers/vendors</CardDescription>
            </CardHeader>
            <CardContent>
              {samplesReceived.length === 0 ? (
                <div className="text-center py-8">
                  <ArrowDownLeft className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">No samples received yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Sample No</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Received From</TableHead>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-center">Qty</TableHead>
                      <TableHead>Condition</TableHead>
                      <TableHead>Action Taken</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {samplesReceived.map((sample) => (
                      <TableRow key={sample.id}>
                        <TableCell className="font-medium">{sample.sample_no}</TableCell>
                        <TableCell>{formatDate(sample.date)}</TableCell>
                        <TableCell>{sample.party_name || sample.received_from}</TableCell>
                        <TableCell className="max-w-[200px] truncate">{sample.item_name}</TableCell>
                        <TableCell className="text-center">{sample.quantity}</TableCell>
                        <TableCell>
                          <Badge variant={sample.condition === 'good' ? 'default' : 'destructive'}>
                            {sample.condition}
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-[150px] truncate">{sample.action_taken || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sales Returns Tab */}
        <TabsContent value="returns">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Sales Returns Register</CardTitle>
              <CardDescription>Goods returned by customers</CardDescription>
            </CardHeader>
            <CardContent>
              {salesReturns.length === 0 ? (
                <div className="text-center py-8">
                  <RotateCcw className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">No sales returns recorded</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Sales returns are created from Store Receiving with "Sales Return" nature of inward
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>SRN</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Items</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {salesReturns.map((ret) => (
                      <TableRow key={ret.id}>
                        <TableCell className="font-medium">{ret.srn}</TableCell>
                        <TableCell>{formatDate(ret.created_at)}</TableCell>
                        <TableCell>{ret.supplier_name}</TableCell>
                        <TableCell>{ret.items?.length || 0} items</TableCell>
                        <TableCell className="max-w-[150px] truncate">{ret.remarks || '-'}</TableCell>
                        <TableCell>{ret.sales_return_action || '-'}</TableCell>
                        <TableCell>
                          <Badge variant={ret.status === 'processed' ? 'default' : 'secondary'}>
                            {ret.status || 'pending'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Issue Sample Dialog */}
      <Dialog open={issueSampleDialog} onOpenChange={setIssueSampleDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Issue Sample</DialogTitle>
            <DialogDescription>Send sample to client/prospect</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Client/Supplier Name *</Label>
              <SearchableSelect
                options={parties.map(p => ({ value: p.name, label: p.name })).sort((a, b) => a.label.localeCompare(b.label))}
                value={issueForm.party_name}
                onValueChange={(v) => setIssueForm({...issueForm, party_name: v})}
                placeholder="Select client or supplier..."
                searchPlaceholder="Search party..."
                emptyText="No parties found"
              />
            </div>
            <div>
              <Label>Item *</Label>
              <SearchableSelect
                options={items.map(i => ({ 
                  value: i.name, 
                  label: i.name,
                  code: i.item_code || '',
                  category: i.category || ''
                })).sort((a, b) => a.label.localeCompare(b.label))}
                value={issueForm.item_name}
                onValueChange={(v) => setIssueForm({...issueForm, item_name: v})}
                placeholder="Select item..."
                searchPlaceholder="Search item..."
                emptyText="No items found"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Quantity *</Label>
                <Input 
                  type="number" 
                  min="1"
                  value={issueForm.quantity} 
                  onChange={(e) => setIssueForm({...issueForm, quantity: e.target.value})}
                />
              </div>
              <div>
                <Label>Sample Type *</Label>
                <Select value={issueForm.sample_type} onValueChange={(v) => setIssueForm({...issueForm, sample_type: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Returnable">Returnable</SelectItem>
                    <SelectItem value="Non-Returnable">Non-Returnable</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Purpose</Label>
              <Input 
                placeholder="e.g., Quality approval, Color check"
                value={issueForm.purpose}
                onChange={(e) => setIssueForm({...issueForm, purpose: e.target.value})}
              />
            </div>
            {issueForm.sample_type === 'Returnable' && (
              <div>
                <Label>Expected Return Date</Label>
                <Input 
                  type="date"
                  value={issueForm.expected_return_date}
                  onChange={(e) => setIssueForm({...issueForm, expected_return_date: e.target.value})}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIssueSampleDialog(false)}>Cancel</Button>
            <Button onClick={handleIssueSample} disabled={!issueForm.party_name || !issueForm.item_name}>
              Issue Sample
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receive Sample Dialog */}
      <Dialog open={receiveSampleDialog} onOpenChange={setReceiveSampleDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Receive Sample</DialogTitle>
            <DialogDescription>Record sample received from supplier/vendor</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Received From *</Label>
              <SearchableSelect
                options={parties.map(p => ({ value: p.name, label: p.name })).sort((a, b) => a.label.localeCompare(b.label))}
                value={receiveForm.party_name}
                onValueChange={(v) => setReceiveForm({...receiveForm, party_name: v})}
                placeholder="Select supplier or client..."
                searchPlaceholder="Search party..."
                emptyText="No parties found"
              />
            </div>
            <div>
              <Label>Item Description *</Label>
              <Input 
                placeholder="Item name/description"
                value={receiveForm.item_name}
                onChange={(e) => setReceiveForm({...receiveForm, item_name: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Quantity *</Label>
                <Input 
                  type="number" 
                  min="1"
                  value={receiveForm.quantity} 
                  onChange={(e) => setReceiveForm({...receiveForm, quantity: e.target.value})}
                />
              </div>
              <div>
                <Label>Condition *</Label>
                <Select value={receiveForm.condition} onValueChange={(v) => setReceiveForm({...receiveForm, condition: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="good">Good</SelectItem>
                    <SelectItem value="damaged">Damaged</SelectItem>
                    <SelectItem value="partial">Partial</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Action Taken</Label>
              <Textarea 
                placeholder="e.g., Sent to QC, Approved for trial"
                value={receiveForm.action_taken}
                onChange={(e) => setReceiveForm({...receiveForm, action_taken: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReceiveSampleDialog(false)}>Cancel</Button>
            <Button onClick={handleReceiveSample} disabled={!receiveForm.party_name || !receiveForm.item_name}>
              Receive Sample
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record Return Dialog */}
      <Dialog open={returnSampleDialog} onOpenChange={setReturnSampleDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Record Sample Return</DialogTitle>
            <DialogDescription>
              {selectedSample && (
                <span>Recording return for {selectedSample.sample_no} - {selectedSample.item_name}</span>
              )}
            </DialogDescription>
          </DialogHeader>
          {selectedSample && (
            <div className="space-y-4">
              <div className="p-3 bg-muted rounded-lg text-sm">
                <p>Original Qty: <strong>{selectedSample.quantity}</strong></p>
                <p>Already Returned: <strong>{selectedSample.returned_qty || 0}</strong></p>
                <p>Pending: <strong>{selectedSample.quantity - (selectedSample.returned_qty || 0)}</strong></p>
              </div>
              <div>
                <Label>Quantity Returned *</Label>
                <Input 
                  type="number" 
                  min="0"
                  max={selectedSample.quantity - (selectedSample.returned_qty || 0)}
                  value={returnForm.returned_qty}
                  onChange={(e) => setReturnForm({...returnForm, returned_qty: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea 
                  placeholder="Any notes about the return condition..."
                  value={returnForm.notes}
                  onChange={(e) => setReturnForm({...returnForm, notes: e.target.value})}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReturnSampleDialog(false)}>Cancel</Button>
            <Button onClick={handleReturnSample} disabled={returnForm.returned_qty <= 0}>
              Record Return
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </DashboardLayout>
  );
}
